import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

/**
 * Switches the active Organization. Updates client state + the X-Organization
 * header (F1) and clears cached org-scoped data so it refetches for the new org.
 */
export function OrgSwitcher() {
  const qc = useQueryClient();
  const organizations = useSession((s) => s.organizations);
  const activeOrgSlug = useSession((s) => s.activeOrgSlug);
  const setActiveOrg = useSession((s) => s.setActiveOrg);

  const active = organizations.find((o) => o.slug === activeOrgSlug);

  if (organizations.length === 0) return null;

  function selectOrg(slug: string) {
    if (slug === activeOrgSlug) return;
    setActiveOrg(slug);
    qc.clear(); // drop all cached server data — it was scoped to the previous org
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="max-w-[12rem] justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-4 shrink-0" />
            <span className="truncate">{active?.name ?? 'Select org'}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onSelect={() => selectOrg(org.slug)}
            className="justify-between"
          >
            <span className="truncate">{org.name}</span>
            <Check className={cn('size-4', org.slug === activeOrgSlug ? 'opacity-100' : 'opacity-0')} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
